'use client'

import type { CSSProperties } from 'react'
import { GolfBall } from './GolfBall'
import type { PuttMark } from './tileViewModel'

type Props = Readonly<{
  mark: PuttMark
  /** Rendered edge length in px — the RING's box, not the ball's. */
  size?: number
  /** When given the glyph is announced on its own; otherwise the tile labels it. */
  label?: string
}>

/**
 * One putt, in the board's single vocabulary (B20 Task 2 item 1): the shared
 * golf ball inside a coloured ring. HOLED = green ring, a green glow and a dark
 * radial under the ball, as if it were sitting down in the cup. MISSED = red
 * ring, the ball greyscaled back to 26 % — present, but not counted.
 *
 * Two promises the drawing depends on, both kept in `birdie-board.scss`:
 *  - the ring is an INSET box-shadow, never a border, so holed and missed
 *    occupy exactly the same box and a ball never shifts when a putt resolves;
 *  - the ball is a percentage of that box, so one `size` sets both.
 *
 * B20 replaced the abstract disc/ring pair this used to draw. The pair read in
 * greyscale, which the ball alone does not — so the RING carries the colour and
 * the missed ball is also desaturated and faded, which is a second, non-colour
 * cue for the same fact.
 */
export function PuttGlyph({ mark, size = 18, label }: Props) {
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true }
  return (
    <span
      className={`birdie-glyph birdie-glyph--${mark}`}
      style={{ '--birdie-glyph-size': `${size}px` } as CSSProperties}
      {...a11y}
    >
      <GolfBall className="birdie-glyph__ball" />
    </span>
  )
}
